import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import ForbiddenPage from "../../components/ForbiddenPage";
import MainContainer from "../../components/MainContainer";
import MetricsStrip from "../../components/MetricsStrip";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  main: { minWidth: 0 },
  stack: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5) },
  hero: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  title: { fontWeight: 600 },
  filters: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  tabsPaper: {
    padding: theme.spacing(0.75),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    overflowX: "auto",
  },
  tab: {
    minHeight: 40,
    borderRadius: 12,
    textTransform: "none",
    fontWeight: 600,
    "&.Mui-selected": { backgroundColor: theme.palette.action.hover },
  },
  section: {
    padding: theme.spacing(2),
    borderRadius: 18,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
  },
  sectionTitle: { fontWeight: 600, marginBottom: theme.spacing(1.5) },
  quotaGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: theme.spacing(1.5),
  },
  quotaCard: {
    padding: theme.spacing(1.5),
    borderRadius: 14,
    border: `1px solid ${theme.palette.divider}`,
  },
  quotaHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
  },
  table: { borderRadius: 14, border: `1px solid ${theme.palette.divider}` },
  incidents: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap" },
  empty: { padding: theme.spacing(4), textAlign: "center" },
}));

const operationLabels = {
  conversation: "Conversas",
  embedding_index: "Indexações RAG",
  embedding_query: "Consultas RAG",
  transcription: "Transcrições",
  synthesis: "Sínteses de voz",
  knowledge_storage: "Armazenamento RAG",
};

const quotaLabels = {
  monthlyTokens: "Tokens totais",
  monthlyEmbeddingTokens: "Tokens de embeddings",
  monthlyProcessedCharacters: "Caracteres processados",
  monthlyIndexRuns: "Indexações",
  monthlyKnowledgeQueries: "Consultas RAG",
  storageBytes: "Armazenamento",
  knowledgeSources: "Fontes de conhecimento",
  concurrentRequests: "Requisições simultâneas",
};

const number = (value) =>
  new Intl.NumberFormat("pt-BR").format(Number(value || 0));
const milliseconds = (value) => (value == null ? "—" : `${number(value)} ms`);
const dateInput = (date) => date.toISOString().slice(0, 10);

const AiUsage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const now = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState(
    dateInput(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))),
  );
  const [endDate, setEndDate] = useState(dateInput(now));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get("/ai/usage-dashboard", {
        params: { startDate, endDate },
      });
      setData(response.data);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (user.profile === "user") {
    return (
      <MainContainer className={classes.main} fullWidth>
        <ForbiddenPage />
      </MainContainer>
    );
  }

  const totals = data?.totals || {};
  const incidents = data?.incidents || {};
  const quota = data?.quota || {
    policy: {},
    used: {},
    reserved: {},
    remaining: {},
  };
  const summary = [
    { key: "requests", label: "Requisições", value: number(totals.requests) },
    {
      key: "success",
      label: "Concluídas",
      value: number(totals.succeeded),
      severity: "success",
    },
    {
      key: "failed",
      label: "Falhas",
      value: number(totals.failed),
      severity: totals.failed ? "error" : undefined,
    },
    { key: "tokens", label: "Tokens medidos", value: number(totals.tokens) },
  ];

  return (
    <MainContainer className={classes.main} fullWidth>
      <div className={classes.stack}>
        <div className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.title}>
              Consumo e saúde da IA
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Acompanhe separadamente conversas, RAG, voz, cotas e falhas do seu
              tenant.
            </Typography>
          </Box>
          <div className={classes.filters}>
            <TextField
              label="De"
              type="date"
              size="small"
              variant="outlined"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Até"
              type="date"
              size="small"
              variant="outlined"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              color="primary"
              variant="contained"
              onClick={load}
              disabled={loading}
            >
              Atualizar
            </Button>
          </div>
        </div>

        <Paper className={classes.tabsPaper}>
          <Tabs
            value="usage"
            onChange={(_event, value) =>
              history.push(
                value === "agents"
                  ? "/prompts"
                  : value === "library"
                    ? "/prompts?section=library"
                    : value === "values"
                      ? "/prompts?section=values"
                      : value === "knowledge"
                        ? "/knowledge-bases"
                        : "/ai-usage",
              )
            }
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab className={classes.tab} value="agents" label="Meus agentes" />
            <Tab
              className={classes.tab}
              value="library"
              label="Biblioteca de templates"
            />
            <Tab className={classes.tab} value="values" label="Variáveis da empresa" />
            <Tab
              className={classes.tab}
              value="knowledge"
              label="Bases de conhecimento"
            />
            <Tab
              className={classes.tab}
              value="usage"
              label="Consumo e saúde"
            />
          </Tabs>
        </Paper>

        <MetricsStrip ariaLabel="Resumo de consumo de IA" items={summary} />

        <Paper className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Cotas do período atual
          </Typography>
          <div className={classes.quotaGrid}>
            {Object.keys(quotaLabels).map((key) => {
              const limit = Number(quota.policy[key] || 0);
              const used = Number(quota.used[key] || 0);
              const reserved = Number(quota.reserved[key] || 0);
              const consumed = used + reserved;
              const percentage = limit
                ? Math.min(100, (consumed / limit) * 100)
                : 0;
              return (
                <div className={classes.quotaCard} key={key}>
                  <div className={classes.quotaHeader}>
                    <Typography variant="body2">{quotaLabels[key]}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {number(consumed)} / {limit ? number(limit) : "Ilimitado"}
                    </Typography>
                  </div>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    color={percentage >= 90 ? "secondary" : "primary"}
                  />
                  {reserved > 0 && (
                    <Typography variant="caption" color="textSecondary">
                      {number(reserved)} reservado(s) por operações em andamento
                    </Typography>
                  )}
                </div>
              );
            })}
          </div>
        </Paper>

        <Paper className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Alertas operacionais
          </Typography>
          <div className={classes.incidents}>
            <Chip
              label={`Falhas de indexação: ${number(incidents.indexingFailures)}`}
              color={incidents.indexingFailures ? "secondary" : "default"}
            />
            <Chip
              label={`Falhas de consulta: ${number(incidents.retrievalFailures)}`}
              color={incidents.retrievalFailures ? "secondary" : "default"}
            />
            <Chip label={`Sem evidência: ${number(incidents.noEvidence)}`} />
            <Chip
              label={`Credenciais inválidas: ${number(incidents.invalidCredentials)}`}
              color={incidents.invalidCredentials ? "secondary" : "default"}
            />
            <Chip
              label={`Fila atrasada: ${number(incidents.delayedQueue)}`}
              color={incidents.delayedQueue ? "secondary" : "default"}
            />
          </div>
        </Paper>

        <Paper className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Consumo por operação
          </Typography>
          <TableContainer className={classes.table}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Operação</TableCell>
                  <TableCell align="right">Requisições</TableCell>
                  <TableCell align="right">Sucesso</TableCell>
                  <TableCell align="right">Falhas</TableCell>
                  <TableCell align="right">Tokens</TableCell>
                  <TableCell align="right">Provedor</TableCell>
                  <TableCell align="right">RAG</TableCell>
                  <TableCell align="right">Canal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.operations || []).map((item) => (
                  <TableRow key={item.operation} hover>
                    <TableCell>
                      {operationLabels[item.operation] || item.operation}
                    </TableCell>
                    <TableCell align="right">{number(item.requests)}</TableCell>
                    <TableCell align="right">
                      {number(item.succeeded)}
                    </TableCell>
                    <TableCell align="right">{number(item.failed)}</TableCell>
                    <TableCell align="right">{number(item.tokens)}</TableCell>
                    <TableCell align="right">
                      {milliseconds(item.averageProviderLatencyMs)}
                    </TableCell>
                    <TableCell align="right">
                      {milliseconds(item.averageRetrievalLatencyMs)}
                    </TableCell>
                    <TableCell align="right">
                      {milliseconds(item.averageChannelDelayMs)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Paper className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Eventos recentes
          </Typography>
          <TableContainer className={classes.table}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Operação</TableCell>
                  <TableCell>Provedor / modelo</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Tentativa</TableCell>
                  <TableCell>Correlação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.recentEvents || []).map((event) => (
                  <TableRow key={event.id} hover>
                    <TableCell>
                      {new Date(event.createdAt).toLocaleString("pt-BR")}
                    </TableCell>
                    <TableCell>
                      {operationLabels[event.operation] || event.operation}
                    </TableCell>
                    <TableCell>
                      {[event.provider, event.model]
                        .filter(Boolean)
                        .join(" / ") || "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={
                          event.status === "failed" ? "secondary" : "primary"
                        }
                        label={
                          event.errorCode ||
                          (event.status === "failed" ? "Falha" : "Concluído")
                        }
                      />
                    </TableCell>
                    <TableCell>{event.attempt}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {event.correlationId}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {!loading && !(data?.recentEvents || []).length && (
            <Box className={classes.empty}>
              <Typography color="textSecondary">
                Ainda não há consumo registrado no período.
              </Typography>
            </Box>
          )}
        </Paper>
      </div>
    </MainContainer>
  );
};

export default AiUsage;
